import {
	Button,
	Inline,
	Icons,
	Stack,
	Text,
	Heading,
	Col,
	Divider,
	Grid,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import * as Validator from "yup"
import { RevocablePermissionFields } from "./EditRoles"
import { IRole, IPermission } from "../Roles"
import { IUser, XHR } from "./store"
import {
	Form,
	validateFormValues,
	TextInputField,
	withServerErrors,
	SubmissionError,
	PhoneInputField,
	SwitchInputField,
	arrayMutators,
} from "@sembark-travel/ui-form"
import { useHasFeatureFlag } from "../Auth"

export interface NewUserCredentials {
	name: string
	email: string
	invited_signup_link: string
	roles: Array<IRole>
	revokable_permissions: Array<IPermission>
	phone_numbers?: Array<{
		country_code: string
		phone_number?: string
		number?: string
	}>
	show_only_assigned_trip_sources: 0 | 1
}
const newUserSchema = Validator.object().shape({
	name: Validator.string()
		.required("Name is required")
		.min(4, "Minimum 4 characters required")
		.max(199, "Maximum 199 characters allowed"),
	email: Validator.string()
		.email("Email must be a valid email address")
		.required("Email field is required"),
	roles: Validator.array()
		.required("Please assign role(s) to the new user")
		.min(1, "Please assign role(s) to the new user"),
})

const validate = validateFormValues(newUserSchema)

export function NewUser({
	onSuccess,
	onCancel,
	origin,
}: {
	onSuccess: (user: IUser) => void
	onCancel?: () => void
	origin?: string
}) {
	const xhr = useXHR()
	const hasMultiCurrency = useHasFeatureFlag("multi_currency")
	return (
		<NewUserForm
			origin={origin}
			hasMultiCurrency={hasMultiCurrency}
			onSubmit={async (values: NewUserCredentials) => {
				const { revokable_permissions, ...rest } = values
				const requestData = {
					...rest,
					roles: values.roles.map((r) => r.id),
					revoked_permissions: (values?.revokable_permissions || []).reduce<
						string[]
					>((acc, p) => {
						if (!p.is_revokable) {
							acc.push(p.name)
						}
						return acc
					}, []),
				}
				const user = await XHR(xhr).invite(requestData)
				onSuccess(user)
			}}
			onCancel={onCancel}
		/>
	)
}

export function NewUserForm({
	onSubmit,
	onCancel,
	origin,
	hasMultiCurrency,
}: {
	onSubmit: (data: NewUserCredentials) => Promise<void>
	onCancel?: () => void
	origin?: string
	hasMultiCurrency?: boolean
}) {
	const initialValues: NewUserCredentials = {
		name: "",
		email: "",
		invited_signup_link: origin ? `${origin}/invited-signup` : "",
		roles: [],
		revokable_permissions: [],
		phone_numbers: [
			{
				country_code: "IN",
				phone_number: "+91",
				number: "",
			},
		],
		show_only_assigned_trip_sources: 0,
	}
	return (
		<Form<NewUserCredentials>
			initialValues={initialValues}
			validate={validate}
			onSubmit={withServerErrors(async ({ phone_numbers, ...values }) => {
				await onSubmit({
					...values,
					phone_numbers: phone_numbers
						?.filter((p) => p.number)
						.map((p) => ({
							...p,
							number: String(p.number).replace(/[^\d]/gi, ""),
						})),
				})
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid gap="4">
						<Col xs={12} sm={5}>
							<Stack gap="1">
								<Heading as="h4" fontSize="lg">
									<Icons.User size="6" /> Basic Details
								</Heading>
								<Text color="muted">
									Provide contact details like name and email address of the
									team member.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<TextInputField
									label="Name"
									name="name"
									type="text"
									required
									placeholder="First Last"
									autoComplete="name"
									help="This will be used to address the invited user"
								/>
								<TextInputField
									label="Email"
									type="email"
									name="email"
									autoComplete="username"
									placeholder="username@domain.com"
									required
									help="User will receive an invitation to this email address"
								/>
								<PhoneInputField
									label="Contact Number(s)"
									name="phone_numbers"
									secondaryLabel="optional"
									help="Used for sharing contact details in Quotes"
								/>
							</Stack>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="4">
						<Col xs={12} sm={5}>
							<Stack gap="1">
								<Heading as="h4" fontSize="lg">
									<Icons.ShieldCheck size="6" /> Roles
								</Heading>
								<Text color="muted">
									Manage access permissions for different modules by assigning
									appropriate role(s).
								</Text>
							</Stack>
						</Col>
						<Col>
							<RevocablePermissionFields hasMultiCurrency={hasMultiCurrency} />
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="4">
						<Col xs={12} sm={5}>
							<Stack gap="1">
								<Heading as="h4" fontSize="lg">
									<Icons.Eye size="6" /> Visibility Settings
								</Heading>
								<Text color="muted">
									Manage visibility of B2B sources for this user.
								</Text>
							</Stack>
						</Col>
						<Col>
							<SwitchInputField
								name="show_only_assigned_trip_sources"
								label="Show only assigned B2B sources"
								help="Only show B2B sources that are assigned to this user"
							/>
						</Col>
					</Grid>
					<input
						hidden
						type="hidden"
						name="invited_signup_link"
						value={initialValues.invited_signup_link}
					/>
					<Divider />
					<Grid gap="4">
						<Col xs={12} sm={{ offset: 5, span: 7 }}>
							<Stack gap="4" paddingLeft={{ sm: "4" }}>
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? (
											"Inviting..."
										) : (
											<>
												<Icons.Mail /> Send Invitation
											</>
										)}
									</Button>
									{onCancel ? (
										<Button
											onClick={onCancel}
											level="tertiary"
											disabled={submitting}
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}
